import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faEnvelope,
  faCalendarAlt,
  faTimes,
  faMapMarked,
} from "@fortawesome/free-solid-svg-icons";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import "../../styles/components/footer.css";
import logo from "../../images/logo.png";

interface ModalContent {
  title: string;
  content: React.ReactNode;
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContent>({
    title: "",
    content: null,
  });
  const [cookieConsent, setCookieConsent] = useState<boolean>(() => {
    return localStorage.getItem("cookieConsent") === "true";
  });
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(() => {
    return localStorage.getItem("cookieConsent") === null;
  });

  const openModal = (title: string, content: React.ReactNode) => {
    setModalContent({ title, content });
    setIsModalOpen(true);
    document.body.style.overflow = "hidden"; // Spriječi scroll na pozadini
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = ""; // Vrati scroll na pozadini
  };

  const handleCookieConsent = (consent: boolean) => {
    setCookieConsent(consent);
    localStorage.setItem("cookieConsent", consent.toString());
    setShowCookieBanner(false);
  };

  const privacyPolicyContent = (
    <div className="legal-content">
      <p>
        Beauty Concept Veronika poštuje vašu privatnost. Ova politika
        privatnosti objašnjava kako prikupljamo, koristimo i štitimo vaše osobne
        podatke.
      </p>
      <h4>Koje podatke prikupljamo?</h4>
      <p>
        Prikupljamo podatke koje nam dobrovoljno pružate prilikom rezervacije
        termina ili kontaktiranja putem naše web stranice, uključujući ime,
        email adresu i broj telefona.
      </p>
      <h4>Kako koristimo vaše podatke?</h4>
      <p>
        Vaše podatke koristimo isključivo za pružanje usluga koje ste zatražili,
        kao što je rezervacija termina ili odgovaranje na vaše upite.
      </p>
      <h4>Sigurnost podataka</h4>
      <p>
        Implementirali smo odgovarajuće sigurnosne mjere kako bismo zaštitili
        vaše osobne podatke od neovlaštenog pristupa, izmjene, otkrivanja ili
        uništenja.
      </p>
    </div>
  );

  const termsContent = (
    <div className="legal-content">
      <p>
        Dobrodošli na web stranicu Beauty Concept Veronika. Korištenjem naše
        stranice pristajete na sljedeće uvjete korištenja.
      </p>
      <h4>Intelektualno vlasništvo</h4>
      <p>
        Svi sadržaji na ovoj web stranici, uključujući tekstove, grafike,
        logotipe, slike i softver, zaštićeni su autorskim pravima i pripadaju
        Beauty Concept Veronika.
      </p>
      <h4>Ograničenje odgovornosti</h4>
      <p>
        Beauty Concept Veronika ne preuzima odgovornost za bilo kakve izravne,
        neizravne, slučajne ili posljedične štete koje proizlaze iz korištenja
        ili nemogućnosti korištenja naše web stranice.
      </p>
      <h4>Rezervacije</h4>
      <p>
        Rezervacije termina putem naše web stranice podložne su dostupnosti i
        potvrdi. Zadržavamo pravo odbiti ili otkazati rezervaciju u bilo kojem
        trenutku.
      </p>
    </div>
  );

  const cookiesContent = (
    <div className="legal-content">
      <p>
        Naša web stranica koristi kolačiće kako bi poboljšala vaše iskustvo
        pregledavanja.
      </p>
      <h4>Što su kolačići?</h4>
      <p>
        Kolačići su male tekstualne datoteke koje se pohranjuju na vašem uređaju
        kada posjetite našu web stranicu. Oni nam pomažu da zapamtimo vaše
        postavke i pružimo vam bolje korisničko iskustvo.
      </p>
      <h4>Koje kolačiće koristimo?</h4>
      <p>
        Koristimo neophodne kolačiće za funkcioniranje stranice, analitičke
        kolačiće za praćenje posjećenosti i kolačiće za društvene mreže.
      </p>
      <h4>Upravljanje kolačićima</h4>
      <p>
        Možete kontrolirati i/ili izbrisati kolačiće po želji. Možete izbrisati
        sve kolačiće koji su već na vašem računalu i možete postaviti većinu
        preglednika da ih blokiraju.
      </p>
      <div className="cookie-buttons">
        <button
          className="cookie-button accept"
          onClick={() => handleCookieConsent(true)}
        >
          Prihvati sve kolačiće
        </button>
        <button
          className="cookie-button reject"
          onClick={() => handleCookieConsent(false)}
        >
          Odbij neobavezne kolačiće
        </button>
      </div>
    </div>
  );

  return (
    <>
      <footer className="footer">
        <div className="footer-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
          <div className="shape shape-6"></div>
          <div className="shape shape-7"></div>
          <div className="shape shape-8"></div>
          <div className="shape shape-9"></div>
          <div className="shape shape-10"></div>
          <div className="shape shape-11"></div>
        </div>

        <div className="footer-main">
          <div className="container">
            <div className="footer-row">
              <div className="footer-column">
                <div className="footer-logo">
                  <img src={logo} alt="Beauty Concept Veronika" />
                  <div className="footer-logo-text">
                    <span className="footer-logo-beauty">Beauty Concept</span>
                    <span className="footer-logo-veronika">Veronika</span>
                  </div>
                </div>
                <div className="footer-cta">
                  <Link to="/booking" className="footer-booking-button">
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="booking-icon"
                    />
                    Rezerviraj termin
                  </Link>
                </div>
              </div>

              <div className="footer-column">
                <h3 className="footer-title">Navigacija</h3>
                <ul className="footer-links">
                  <li>
                    <Link to="/">Početna</Link>
                  </li>
                  <li>
                    <Link to="/services">Usluge</Link>
                  </li>
                  <li>
                    <Link to="/gallery">Galerija</Link>
                  </li>
                  <li>
                    <Link to="/about">O nama</Link>
                  </li>
                  <li>
                    <Link to="/contact">Kontakt</Link>
                  </li>
                  <li>
                    <Link to="/booking">Rezervacije</Link>
                  </li>
                </ul>
              </div>

              <div className="footer-column contact-column">
                <h3 className="footer-title">Kontaktirajte nas</h3>
                <ul className="footer-contact-info">
                  <li>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    <span>Ulica grada Vukovara 226G, Zagreb</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faEnvelope} />
                    <a href="mailto:info@beautyconceptveronika.hr">
                      info@beautyconceptveronika.hr
                    </a>
                  </li>
                  <li className="social-media-item">
                    <FontAwesomeIcon icon={faInstagram} />
                    <a
                      href="https://instagram.com/beautyconceptveronika"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      @beautyconceptveronika
                    </a>
                  </li>
                  <li className="map-link">
                    <FontAwesomeIcon icon={faMapMarked} />
                    <a
                      href="https://maps.app.goo.gl/gJtTXcsimDefDCoz5"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Pronađite nas na karti
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="container">
            <div className="footer-bottom-content">
              <p className="copyright">
                &copy; {currentYear} Beauty Concept Veronika. Sva prava
                pridržana.
              </p>
              <ul className="footer-legal">
                <li>
                  <button
                    className="legal-button"
                    onClick={() =>
                      openModal("Politika privatnosti", privacyPolicyContent)
                    }
                  >
                    Privatnost
                  </button>
                </li>
                <li>
                  <button
                    className="legal-button"
                    onClick={() => openModal("Uvjeti korištenja", termsContent)}
                  >
                    Uvjeti korištenja
                  </button>
                </li>
                <li>
                  <button
                    className="legal-button"
                    onClick={() => openModal("Kolačići", cookiesContent)}
                  >
                    Kolačići
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal za pravne informacije */}
      {isModalOpen && (
        <div className="legal-modal-overlay" onClick={closeModal}>
          <div className="legal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="legal-modal-header">
              <h2>{modalContent.title}</h2>
              <button className="close-modal" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="legal-modal-body">{modalContent.content}</div>
          </div>
        </div>
      )}

      {/* Banner za kolačiće */}
      {showCookieBanner && (
        <div className="cookie-banner">
          <div className="cookie-banner-content">
            <p>
              Koristimo kolačiće kako bismo vam pružili najbolje iskustvo na
              našoj web stranici.
              <button
                className="cookie-info-button"
                onClick={() => openModal("Kolačići", cookiesContent)}
              >
                Saznajte više
              </button>
            </p>
            <div className="cookie-banner-buttons">
              <button
                className="cookie-button accept"
                onClick={() => handleCookieConsent(true)}
              >
                Prihvati
              </button>
              <button
                className="cookie-button reject"
                onClick={() => handleCookieConsent(false)}
              >
                Odbij
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
